import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, PageHeader, Button } from "@/components/ui";
import { Clock, DollarSign, Sparkles, ShieldCheck, Download, Loader2, Film } from "lucide-react";

const BENEFITS = [
  { icon: Clock, title: "Save Time", text: "AI drafts summaries, care plans, and letters — 45-minute tasks done in seconds." },
  { icon: DollarSign, title: "Save Money", text: "One platform replaces 3–6 vendors and lifts quality bonuses worth millions." },
  { icon: Sparkles, title: "AI Native", text: "Configurable AI woven through the workflow, not bolted on afterward." },
  { icon: ShieldCheck, title: "Audit-Ready", text: "Built to HIPAA, SOC 2, and NIST 800-53 standards, with true tenant data isolation." },
];

const DEFAULT_VIDEO = "/media/gomco-tour.mp4";

export default function ProductTour() {
  const { user } = useAuth();
  const [tenant, setTenant] = useState(null);
  const pollRef = useRef(null);

  const loadCtx = () => api.get("/context").then((r) => setTenant(r.data.tenant)).catch(() => {});
  useEffect(() => { loadCtx(); }, []);

  useEffect(() => {
    const processing = tenant?.video_status === "processing";
    if (processing && !pollRef.current) pollRef.current = setInterval(loadCtx, 6000);
    if (!processing && pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    return () => { if (pollRef.current && !processing) { clearInterval(pollRef.current); pollRef.current = null; } };
  }, [tenant]);

  const isReady = tenant?.video_status === "ready" && tenant?.video_url;
  const videoUrl = isReady ? tenant.video_url : DEFAULT_VIDEO;
  const isProcessing = tenant?.video_status === "processing";
  const brandName = tenant?.name || "Go MCO";

  return (
    <div className="vc-fade-up max-w-5xl">
      <PageHeader title="Product Tour" subtitle={`A guided walkthrough of ${brandName} — how it works and how it saves MCOs time and money.`} />

      {isProcessing && (
        <div className="flex items-center gap-2 rounded-md bg-amber-50 border border-amber-200 p-3 text-sm text-amber-800 mb-4" data-testid="tour-processing">
          <Loader2 className="h-4 w-4 animate-spin" /> A new custom video is being generated for {brandName}. Showing the standard tour meanwhile.
        </div>
      )}

      <Card className="overflow-hidden mb-6">
        <video key={videoUrl} controls playsInline preload="auto" className="w-full bg-brand-950" data-testid="tour-video">
          <source src={videoUrl} type="video/mp4" />
          Your browser does not support the video tag.
        </video>
      </Card>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {BENEFITS.map((b) => (
          <Card key={b.title} className="p-5" hover>
            <div className="h-10 w-10 rounded-md bg-brand-50 flex items-center justify-center mb-3">
              <b.icon className="h-5 w-5 text-brand-700" strokeWidth={1.75} />
            </div>
            <h3 className="font-display font-semibold">{b.title}</h3>
            <p className="text-sm text-muted-foreground mt-1">{b.text}</p>
          </Card>
        ))}
      </div>

      <Card className="p-6 bg-brand-950 text-white border-0 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h3 className="font-display text-lg font-bold">Share the tour</h3>
          <p className="text-sm text-brand-100">Download the MP4 to embed in decks, emails, or your website.</p>
        </div>
        <div className="flex gap-2">
          {user?.role === "admin" && (
            <Link to="/tenants">
              <Button variant="subtle" data-testid="customize-tour"><Film className="h-4 w-4" /> Customize Video</Button>
            </Link>
          )}
          <a href={videoUrl} download>
            <Button variant="subtle" data-testid="download-tour"><Download className="h-4 w-4" /> Download Video</Button>
          </a>
        </div>
      </Card>
    </div>
  );
}
